import React, { useState } from "react";
import { useGetQuestionsQuery, useCreateQuestionsMutation, useDeleteProductQuestionMutation } from "state/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Plus, Edit, Trash2, Filter, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Loader2, HelpCircle, FileText, PlusCircle } from "lucide-react";
import { toast } from 'react-toastify';

const Questions = () => {
  const { data: questions, isLoading, refetch, error } = useGetQuestionsQuery();
  

  const [createQuestions, { isLoading: isCreating }] = useCreateQuestionsMutation();
  const [deleteProductQuestion, { isLoading: isDeleting }] = useDeleteProductQuestionMutation();

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");

  // Dialog states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  // Form states
  const [questionType, setQuestionType] = useState("");
  const [questionCount, setQuestionCount] = useState("");



  // Handle different data structures
  const questionsArray = Array.isArray(questions) ? questions : 
                        questions?.data ? questions.data : 
                        questions?.questions ? questions.questions : 
                        questions?.result ? questions.result :
                        [];

  // Filter and search functionality
  const filteredQuestions = questionsArray.filter(question => {
    const questionText = question.title || question.question || question.questionText || question.text || question.content || '';
    const questionType = question.questionFormat || question.type || question.questionType || '';
    const productName = question.productName || question.product?.name || question.productId || '';
    
    const matchesSearch = questionText.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         questionType.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         productName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === "ALL" || questionType === typeFilter;
    return matchesSearch && matchesType;
  });

  // Pagination calculations
  const totalItems = filteredQuestions.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentQuestions = filteredQuestions.slice(startIndex, endIndex);

  // Pagination handlers
  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToFirstPage = () => {
    setCurrentPage(1);
  };

  const goToLastPage = () => {
    setCurrentPage(totalPages);
  };

  const goToPreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  // Dialog handlers
  const handleCreateQuestions = async () => {
    if (!questionType || !questionCount) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await createQuestions({ type: questionType, count: parseInt(questionCount) });
      toast.success('Questions created successfully!');
      setIsCreateDialogOpen(false);
      resetForm();
      refetch();
    } catch (error) {
      toast.error('Failed to create questions');
      console.error('Error creating questions:', error);
    }
  };

  const handleDeleteQuestion = async () => {
    try {
      await deleteProductQuestion(selectedQuestion._id);
      toast.success('Question deleted successfully!');
      setIsDeleteDialogOpen(false);
      setSelectedQuestion(null);
      refetch();
    } catch (error) {
      toast.error('Failed to delete question');
      console.error('Error deleting question:', error);
    }
  };

  const handleDelete = (question) => {
    setSelectedQuestion(question);
    setIsDeleteDialogOpen(true);
  };

  const resetForm = () => {
    setQuestionType("");
    setQuestionCount("");
    setSelectedQuestion(null);
  };

  // Statistics calculations
  const totalQuestions = questionsArray.length || 0;
  const mathQuestions = questionsArray.filter(q => (q.questionFormat || q.type || q.questionType) === "math").length || 0;
  const generalQuestions = questionsArray.filter(q => (q.questionFormat || q.type || q.questionType) === "general").length || 0;
  const uniqueProducts = new Set(questionsArray.map(q => q.productName || q.product?.name || q.productId).filter(Boolean)).size || 0;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header Section */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Question Management</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Manage and organize questions across your platform
              </p>
            </div>
            <Button 
              onClick={() => setIsCreateDialogOpen(true)}
              className="bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
            >
              <Plus className="h-4 w-4 mr-2" />
              Generate Questions
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Questions</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalQuestions}</p>
                </div>
                <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                  <HelpCircle className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Math Questions</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{mathQuestions}</p>
                </div>
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">General Questions</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{generalQuestions}</p>
                </div>
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">This Page</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{currentQuestions.length}</p>
                </div>
                <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
                  <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Table Card */}
        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-sm">
          <CardHeader className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white">Question List</CardTitle>
                <CardDescription className="text-sm text-gray-600 dark:text-gray-400">
                  Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} questions
                </CardDescription>
              </div>
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search questions..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-64 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
                  />
                </div>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  <option value="ALL">All Formats</option>
                  <option value="a+b">Math (a+b)</option>
                  <option value="aa+b">Math (aa+b)</option>
                  <option value="a-b">Math (a-b)</option>
                  <option value="aa-b">Math (aa-b)</option>
                  <option value="a*b">Math (a*b)</option>
                  <option value="aa*b">Math (aa*b)</option>
                  <option value="a/b">Math (a/b)</option>
                  <option value="aa/b">Math (aa/b)</option>
                  <option value="a%b">Math (a%b)</option>
                  <option value="aa%b">Math (aa%b)</option>
                </select>
                <Button variant="outline" size="sm" className="border-gray-300 dark:border-gray-600">
                  <Filter className="h-4 w-4 mr-2" />
                  Filter
                </Button>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-gray-600 dark:text-gray-400" />
                <span className="ml-3 text-gray-600 dark:text-gray-400">Loading questions...</span>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Error loading questions</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">There was an error loading the questions. Please try again.</p>
                <Button 
                  onClick={() => refetch()}
                  className="bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
                >
                  Retry
                </Button>
              </div>
            ) : questionsArray && questionsArray.length > 0 ? (
              <div>
                {/* Desktop Table */}
                <div className="overflow-x-auto">
                  <Table className="hidden lg:table">
                    <TableHeader>
                      <TableRow className="bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <TableHead className="font-semibold text-gray-900 dark:text-white">Sr.No</TableHead>
                        <TableHead className="font-semibold text-gray-900 dark:text-white">Question</TableHead>
                        <TableHead className="font-semibold text-gray-900 dark:text-white">Format</TableHead>
                        <TableHead className="font-semibold text-gray-900 dark:text-white">Options</TableHead>
                        <TableHead className="font-semibold text-gray-900 dark:text-white">Correct</TableHead>
                        <TableHead className="font-semibold text-gray-900 dark:text-white">Status</TableHead>
                        <TableHead className="font-semibold text-gray-900 dark:text-white">Created Date</TableHead>
                        <TableHead className="font-semibold text-gray-900 dark:text-white text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {currentQuestions.map((question, index) => (
                        <TableRow 
                          key={question._id} 
                          className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                        >
                          <TableCell className="py-1 text-xs text-gray-900 dark:text-white">{startIndex + index + 1}</TableCell>
                          <TableCell className="py-1">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white text-xs">
                                {question.title || question.question || question.questionText || question.text || question.content || 'N/A'}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">ID: {question._id || question.id}</p>
                            </div>
                          </TableCell>
                          <TableCell className="py-1">
                            <span className="inline-block bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 px-1 py-0.5 rounded text-xs">
                              {question.questionFormat || 'N/A'}
                            </span>
                          </TableCell>
                          <TableCell className="py-1 text-xs text-gray-900 dark:text-white">
                            <div className="text-xs space-y-0.5">
                              <div>A: {question.optionA || 'N/A'}</div>
                              <div>B: {question.optionB || 'N/A'}</div>
                              <div>C: {question.optionC || 'N/A'}</div>
                              <div>D: {question.optionD || 'N/A'}</div>
                            </div>
                          </TableCell>
                          <TableCell className="py-1 text-xs text-gray-900 dark:text-white">
                            <span className="inline-block bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 px-1 py-0.5 rounded text-xs">
                              {question.correctOption || 'N/A'}
                            </span>
                          </TableCell>
                          <TableCell className="py-1 text-xs text-gray-900 dark:text-white">
                            <span className={`inline-block px-1 py-0.5 rounded text-xs ${
                              question.status === 'APPROVED' 
                                ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                                : 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400'
                            }`}>
                              {question.status || 'N/A'}
                            </span>
                          </TableCell>
                          <TableCell className="py-2 text-sm text-gray-900 dark:text-white">
                            {question.timestamp ? new Date(question.timestamp).toLocaleDateString() : 'N/A'}
                          </TableCell>
                          <TableCell className="py-2 text-right">
                            <div className="flex items-center justify-end space-x-1">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDelete(question)}
                                className="border-red-300 dark:border-red-600 text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 h-8 px-2"
                              >
                                <Trash2 className="h-3 w-3 mr-1" />
                                Delete
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Mobile Cards */}
                <div className="lg:hidden space-y-4 p-4">
                  {currentQuestions.map((question, index) => (
                    <Card key={question._id} className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h3 className="font-medium text-gray-900 dark:text-white">
                              {question.title || question.question || question.questionText || question.text || question.content || 'N/A'}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">ID: {question._id || question.id}</p>
                          </div>
                          {(question.questionFormat || question.type || question.questionType) === "math" ? (
                            <span className="inline-block bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 px-2 py-1 rounded text-xs">
                              MATH
                            </span>
                          ) : (
                            <span className="inline-block bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 px-2 py-1 rounded text-xs">
                              GENERAL
                            </span>
                          )}
                        </div>
                        <div className="space-y-2 mb-3">
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            Format: {question.questionFormat || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            Options: A: {question.optionA || 'N/A'}, B: {question.optionB || 'N/A'}, C: {question.optionC || 'N/A'}, D: {question.optionD || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            Correct: {question.correctOption || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            Status: {question.status || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            Created: {question.timestamp ? new Date(question.timestamp).toLocaleDateString() : 'N/A'}
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(question)}
                            className="border-red-300 dark:border-red-600 text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex-1"
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                    <div className="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300">
                      <span>
                        Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} results
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={goToFirstPage}
                        disabled={currentPage === 1}
                        className="border-gray-300 dark:border-gray-600"
                      >
                        <ChevronsLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                        className="border-gray-300 dark:border-gray-600"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      
                      {/* Page Numbers */}
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <Button
                              key={pageNum}
                              variant={currentPage === pageNum ? "default" : "outline"}
                              size="sm"
                              onClick={() => goToPage(pageNum)}
                              className={`w-8 h-8 p-0 ${
                                currentPage === pageNum 
                                  ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900" 
                                  : "border-gray-300 dark:border-gray-600"
                              }`}
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        className="border-gray-300 dark:border-gray-600"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={goToLastPage}
                        disabled={currentPage === totalPages}
                        className="border-gray-300 dark:border-gray-600"
                      >
                        <ChevronsRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <HelpCircle className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No questions found</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">Get started by generating your first questions</p>
                <Button 
                  onClick={() => setIsCreateDialogOpen(true)}
                  className="bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Generate Questions
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Questions Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="w-[95vw] max-w-md sm:max-w-lg bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-white">Generate Questions</DialogTitle>
            <DialogDescription className="text-gray-600 dark:text-gray-400">
              Generate new questions for your platform
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Question Format</label>
              <Select value={questionType} onValueChange={setQuestionType}>
                <SelectTrigger className="mt-1 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700">
                  <SelectValue placeholder="Select question format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="a+b">Math Addition (a+b)</SelectItem>
                  <SelectItem value="aa+b">Math Addition (aa+b)</SelectItem>
                  <SelectItem value="a-b">Math Subtraction (a-b)</SelectItem>
                  <SelectItem value="aa-b">Math Subtraction (aa-b)</SelectItem>
                  <SelectItem value="a*b">Math Multiplication (a*b)</SelectItem>
                  <SelectItem value="aa*b">Math Multiplication (aa*b)</SelectItem>
                  <SelectItem value="a/b">Math Division (a/b)</SelectItem>
                  <SelectItem value="aa/b">Math Division (aa/b)</SelectItem>
                  <SelectItem value="a%b">Math Modulo (a%b)</SelectItem>
                  <SelectItem value="aa%b">Math Modulo (aa%b)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Number of Questions</label>
              <Input
                type="number"
                placeholder="Enter number of questions"
                value={questionCount}
                onChange={(e) => setQuestionCount(e.target.value)}
                className="mt-1 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
              />
            </div>
          </div>
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setIsCreateDialogOpen(false);
                resetForm();
              }}
              className="w-full sm:w-auto border-gray-300 dark:border-gray-600"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateQuestions}
              disabled={isCreating}
              className="w-full sm:w-auto bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Generate Questions
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="w-[95vw] max-w-md bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-white">Delete Question</DialogTitle>
            <DialogDescription className="text-gray-600 dark:text-gray-400">
              Are you sure you want to delete this question? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setIsDeleteDialogOpen(false)}
              className="w-full sm:w-auto border-gray-300 dark:border-gray-600"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteQuestion}
              disabled={isDeleting}
              className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Question'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Questions;
